import "./scrollable.css";
import "./consignment-page.scss";
import {useParams, useSearchParams} from "react-router-dom";
import {Collapsible, Page} from "@nike/epic-react-ui";
import {Banner, Button, Card, IconButton, Link, StatusIndicator, Tab, TabGroup, Tag, Text} from "@nike/eds";
import type {TableRef} from "../components/base-table";
import {Consignment, ConsignmentAuditExceptionEntry, ConsignmentAuditStepEntry, CustomsEntryDocument, CustomsStatusDocument, DeclarationParty, Exception, FilingPackDocument, User} from "../graphql/graphql";
import {ConsignmentWorkflow} from "../components/consignment-workflow/ConsignmentWorkflow";
import {ApolloError, useApolloClient, useQuery} from '@apollo/client';
import {GET_CONSIGNMENT} from "../graphql/consignment.graphql";
import {useEffect, useMemo, useReducer, useRef, useState} from "react";
import {ShipmentsProducts} from "../components/shipments-products/ShipmentsProducts";
import {DeclarationStatusTable} from "../components/declaration-status-table/DeclarationStatusTable";
import {formatDate, formatDateTime, mapHealthStatusToIndicator, mapStatusToColor} from "../shared/Utils";
import {GqlErrorModal} from "../shared/GqlErrorModal";
import {ContainerNumbersPopover} from "../shared/ContainerNumbersPopover";
import {PageSpinner} from "../shared/PageSpinner";
import {UserUISettingsService} from "../services/UserUISettingsService";
import {ConsignmentDocumentsTab} from "../components/consignment-documents/ConsignmentDocumentsTab";
import {ConsignmentInvolvedPartiesTab} from "../components/consignment-involved-parties/ConsignmentInvolvedPartiesTab";
import {CaretCircleLeft, CaretCircleRight, Loading, Replay} from "@nike/nike-design-system-icons";

type ConsignmentPageProps = {
    history: any;
    currentTerritories: string[];
    currentUser?: User | undefined;
};

type ConsignmentPageState = {
    loading: boolean;
    error: ApolloError | null;
    data: Consignment | null;
};

type ConsignmentPageAction =
    | { type: "FETCH_START" }
    | { type: "FETCH_SUCCESS"; payload: Consignment | null }
    | { type: "FETCH_ERROR"; payload: any }


function consignmentPageReducer(state: ConsignmentPageState, action: ConsignmentPageAction): ConsignmentPageState {
    switch (action.type) {
        case "FETCH_START":
            return {...state, loading: true, error: null};
        case "FETCH_SUCCESS":
            return {...state, loading: false, error: null, data: action.payload};
        case "FETCH_ERROR":
            return {...state, loading: false, error: action.payload};
        default:
            return state;
    }
}

export const ConsignmentPage = ({history, currentTerritories, currentUser}: ConsignmentPageProps) => {

    const [searchParams, setSearchParams] = useSearchParams();
    const {id} = useParams<{ id?: string }>();

    const TAB_IDS = [
        "shipments",
        "declaration",
        "involved-parties",
        "documents",
        "exceptions",
        "audit"
    ];

    const tabParam = searchParams.get("tab");
    const stepParam = searchParams.get("stepName") || null;

    const initialTab = TAB_IDS.includes(tabParam || "") ? tabParam : "shipments";
    const [activeTab, setActiveTab] = useState(initialTab);
    // Track which exception step should be expanded
    const [expandedExceptionStep, setExpandedExceptionStep] = useState<string | null>(stepParam);

    const [showErrorModal, setShowErrorModal] = useState(true);

    const client = useApolloClient();
    // Detect E2E test runtime (set via page.addInitScript in Playwright)
    const isE2E = typeof window !== 'undefined' && (window as any).__E2E__ === true;
    // In E2E mode, prefer localStorage-backed settings to avoid noisy network-only queries / remount churn
    const settingsService = useMemo(() => new UserUISettingsService(client, isE2E), [client, isE2E]);
    const [settings, setSettings] = useState<any>(null);
 
    useEffect(() => {
        // Skip remote settings fetch in E2E to reduce GraphQL chatter & DOM re-renders
        if (isE2E) {
            try {
                const existing = localStorage.getItem('uiSettings');
                if (existing) {
                    setSettings(JSON.parse(existing));
                } else {
                    // Seed a minimal settings blob so pageSize persistence logic works without mutation round-trip
                    const seed = { consignmentsBannerDismissed: 'false' } as any;
                    localStorage.setItem('uiSettings', JSON.stringify(seed));
                    setSettings(seed);
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('[E2E] Failed to parse or seed uiSettings', e);
            }
        return;
        }
        let mounted = true;
        settingsService.getSettings().then(result => {
            if (mounted) setSettings(result);
        }).catch(err => {
            if (mounted) console.error('Failed to load settings:', err);
        });
        return () => { mounted = false; };
    }, [settingsService, isE2E]);

    const handleDismissBanner = async () => {
        await settingsService.changeSettings({...settings, consignmentsBannerDismissed: true});
        const updatedSettings = await settingsService.getSettings();
        setSettings(updatedSettings);
    };

    const [showBanner, setShowBanner] = useState(false);

    // below should change to look for boolean true if resolver gets updated
    useEffect(() => {
        // Only update if settings is loaded
        if (settings) {
            setShowBanner((!(settings.consignmentBannerDismissed === 'true') || !(settings.consignmentBannerDismissed === true)) && ((consignment?.actualShipmentCount ?? 0) > 1));
        }
    }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

    const [state, dispatch] = useReducer(consignmentPageReducer, {
        loading: false,
        error: null,
        data: null,
    });

    const skipLoading = !id || !currentUser;
    // Fetch consignment data if id is present
    const {loading, error, data, refetch} = useQuery(GET_CONSIGNMENT, {
        skip: skipLoading,
        variables: {id},
    });

    useEffect(() => {
        if (loading) {
            dispatch({type: "FETCH_START"});
        }
    }, [loading]);

    useEffect(() => {
        if (data && data.consignment?.get) {
            dispatch({type: "FETCH_SUCCESS", payload: data.consignment.get});
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            dispatch({type: "FETCH_ERROR", payload: error});
        }
    }, [error]);

    const consignment = state.data || undefined;

    const [workflowCollapsed, setWorkflowCollapsed] = useState(false);
    const [hideWorkflowCard, setHideWorkflowCard] = useState(false);

    useEffect(() => {
        if (workflowCollapsed) {
            // Wait for the CSS transition to finish before hiding the Card
            const timeout = setTimeout(() => setHideWorkflowCard(true), 400); // match transition duration
            return () => clearTimeout(timeout);
        } else {
            setHideWorkflowCard(false);
        }
    }, [workflowCollapsed]);

    const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
    const [refreshing, setRefreshing] = useState(false);

    // Update lastRefreshed on initial load
    useEffect(() => {
        if (!loading && consignment) {
            setLastRefreshed(new Date());
        }
    }, [loading, consignment]);

    // Update the refresh handler
    const handleRefresh = async () => {
        setRefreshing(true);
        await refetch();
        setLastRefreshed(new Date());
        setRefreshing(false);
    };

    return (
        <Page
            documentTitle="Consignment - Customs Compliance Hub"
            width="full"
            className="scrollable consignment-page"
        >

            {loading && !error && <PageSpinner/>}
            {error && <Text font="body-3" as="div" color="danger">Error loading consignment.</Text>}

            {!loading && !error && consignment && (
                <>
                    <ConsignmentTop consignment={consignment} onRefresh={handleRefresh} lastRefreshed={lastRefreshed} refreshing={refreshing}/>

                    <div className="eds-flex eds-flex--direction-column eds-flex--l-direction-row eds-spacing--mb-24 eds-gap--32">
                        {/** Workflow Drawer **/}

                        <Card className={hideWorkflowCard ? "workflow-drawer-hidden" : ""}>
                            <div className={`workflow-drawer${workflowCollapsed ? " collapsed" : ""}`}>
                                <div className="eds-flex eds-flex--justify-content-space-between eds-flex--align-items-center eds-spacing--mb-24">
                                    <Text font={'title-3'} as={'h2'}>
                                        Workflow
                                    </Text>
                                    <IconButton
                                        label={"Hide Workflow"}
                                        variant="ghost"
                                        onClick={() => setWorkflowCollapsed(c => !c)}
                                        size="small"
                                        title="Hide Workflow"
                                        icon={<CaretCircleLeft/>}
                                    />
                                </div>
                                {consignment?.steps && <ConsignmentWorkflow steps={consignment.steps}/>}
                            </div>
                        </Card>

                        <div className="eds-flex--grow-1">
                            <TabGroup
                                name="consignment-tabs"
                                activeId={activeTab ?? undefined}
                                onChange={e => {
                                    const newTab = e.target.id;
                                    setActiveTab(newTab);

                                    // Clone current params
                                    const params = new URLSearchParams(searchParams);

                                    if (newTab === "shipments") {
                                        params.delete("tab"); // Remove tab param for default
                                    } else {
                                        params.set("tab", newTab); // Set tab param for others
                                    }

                                    setSearchParams(params, {replace: true});
                                }}
                            >
                                {workflowCollapsed && (
                                    <Tab id="show-workflow" className="show-workflow-tab">
                                        <IconButton
                                            className="eds-flex--align-items-center"
                                            label={"Show Workflow"}
                                            variant="ghost"
                                            onClick={() => setWorkflowCollapsed(c => !c)}
                                            size="small"
                                            title="Show Workflow"
                                            icon={<CaretCircleRight/>}
                                        />
                                    </Tab>)}
                                <Tab id="shipments">Shipments &amp; Products</Tab>
                                <Tab id="declaration">Declaration</Tab>
                                <Tab id="involved-parties">Parties Involved</Tab>
                                <Tab id="documents">Documents</Tab>
                                <Tab id="exceptions">Exceptions ({consignment.openExceptionCount || 0}) </Tab>
                                <Tab id="audit">Audit Trail</Tab>
                            </TabGroup>
                            <div className="eds-spacing--mt-24">
                                {activeTab === "shipments" && (
                                    <Card>
                                        {showBanner && <>
                                            <Banner
                                                status="info"
                                                buttonSlot={
                                                    <Button variant="ghost" onClick={() => handleDismissBanner()} disableRipple>
                                                        Dismiss
                                                    </Button>
                                                }
                                            > Click on a row to view products contained in the shipment</Banner>
                                            <div className="eds-spacing--mt-16"></div>
                                        </>}

                                        {/* <Button
                                            variant="ghost"
                                            onClick={handleResetBanner}
                                            className="eds-spacing--mb-16"
                                        >
                                            Show Banner Again
                                        </Button> */}

                                        <ShipmentsProducts consignmentId={consignment.id} currentUser={currentUser}/>
                                    </Card>
                                )}

                                {activeTab === "declaration" && (
                                    <DeclarationTab docs={(consignment.documents?.filter((d): d is CustomsStatusDocument => !!d) || [])}/>
                                )}

                                {activeTab === "involved-parties" && (
                                    <ConsignmentInvolvedPartiesTab
                                        involvedParties={
                                            (consignment.involvedParties ?? []).filter(
                                                (party): party is DeclarationParty => !!party
                                            )
                                        }
                                    />
                                )}

                                {activeTab === "documents" && (
                                    <ConsignmentDocumentsTab
                                        documents={(consignment.documents?.filter((doc): doc is (CustomsEntryDocument | CustomsStatusDocument | FilingPackDocument) => !!doc) || [])}
                                        consignmentId={consignment.id}
                                    />
                                )}

                                {activeTab === "exceptions" && (
                                    <ExceptionsTab
                                        exceptions={consignment.exceptions?.items || []}
                                        dispatch={dispatch}
                                        expandedStep={expandedExceptionStep}
                                        onExpandStep={step => {
                                            setExpandedExceptionStep(step);
                                        }}
                                    />
                                )}

                                {activeTab === "audit" && (
                                    <AuditTrailTab auditTrailItems={consignment.auditTrail?.items || []}/>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {showErrorModal && state.error && (
                <GqlErrorModal error={state.error} onClose={() => setShowErrorModal(false)}/>
            )}

        </Page>
    );
}

const ConsignmentTop = ({consignment, onRefresh, lastRefreshed, refreshing}: { consignment: Consignment, onRefresh: () => void, lastRefreshed: Date | null, refreshing: boolean }) => (
    <Card className="eds-spacing--mb-32">
        <div className="eds-flex eds-flex--direction-column eds-flex--l-direction-row eds-flex--justify-content-space-between eds-spacing--16 eds-gap--16 eds-spacing--mb-16">
            <div>
                <Link font={'body-1'} as="a" href={`/transport-vehicles/`}>Transport Vehicles</Link><Text as="span" font="body-1">&nbsp;&gt;&nbsp;</Text>
                <Link font={'body-1'} as="a" href={`/transport-vehicles/${consignment?.equipmentVehicle?.id}`}>{consignment?.equipmentVehicle?.voyageNumber}</Link>
                <Text as="span" font="body-1">&nbsp;&gt;&nbsp;</Text>
                <Text as="span" font="body-1">{consignment.id}</Text>
            </div>
            <div className="eds-flex eds-flex--align-items-center">
                {lastRefreshed && (
                    <Text font="body-3" as="span" className="">
                        Last refreshed: {formatDateTime(lastRefreshed.toISOString())}
                    </Text>
                )}
                {onRefresh && (
                    <IconButton
                        variant="ghost"
                        size="small"
                        icon={refreshing ? <Loading/> : <Replay/>}
                        label={refreshing ? "Refreshing..." : "Reload"}
                        className={refreshing ? "rotate-360" : ""}
                        onClick={() => onRefresh()}
                        disabled={refreshing}
                        disableRipple
                    >
                        Refresh
                    </IconButton>
                )}
            </div>

        </div>
        <div className="eds-flex eds-flex--direction-column eds-flex--l-direction-row eds-flex--justify-content-space-between eds-spacing--16 eds-gap--16">
            {consignment && (
                <>
                    <div className="consignment-details-top">
                        <Text font="body-3" as="div" className="eds-spacing--mb-4">ETA</Text>
                        {consignment?.equipmentVehicle?.plannedDischargeDate ? <Text font="body-1" as="p">{formatDate(consignment.equipmentVehicle.plannedDischargeDate)}</Text> : <Text font="body-1" as="p">NA</Text>}
                    </div>
                    <div className="consignment-details-top">
                        <Text font="body-3" as="div" className="eds-spacing--mb-4">Health</Text>
                        {consignment?.health?.health ? <StatusIndicator className="" size='x-large' label={consignment.health?.health} status={mapHealthStatusToIndicator(consignment.health?.health)}/> : null}
                    </div>
                    <div className="consignment-details-top">
                        <Text font="body-3" as="div">MOT</Text>
                        <Text font="body-1" as="p">{consignment.modeOfTransport}</Text>
                    </div>
                    <div className="consignment-details-top">
                        <Text font="body-3" as="div">BOL Number</Text>
                        <Text font="body-1" as="p">{consignment.billOfLadingNbr}</Text>
                    </div>
                    <div className="consignment-details-top">
                        <Text font="body-3" as="div">Container Number</Text>
                        <Text
                            className={consignment.containerNbrs ?? [].length > 1 ? "ellipsis" : undefined}
                            font="body-1"
                            as="p">
                            {consignment.containerNbrs?.join(", ")}
                        </Text>
                        {(consignment.containerNbrs && consignment.containerNbrs.length > 1) ? (
                            <ContainerNumbersPopover containerNumbers={(consignment.containerNbrs ?? []).filter((c): c is string => !!c)}/>
                        ) : null}
                    </div>
                    <div className="consignment-details-top">
                        <Text font="body-3" as="div">Carrier</Text>
                        <Text font="body-1" as="p">{consignment.carrier}</Text>
                    </div>
                    <div className="consignment-details-top">
                        <Text font="body-3" as="div">Export Country</Text>
                        <Text font="body-1" as="p">{consignment.originCountry}</Text>
                    </div>
                    <div className="consignment-details-top">
                        <Text font="body-3" as="div">Discharge Country</Text>
                        <Text font="body-1" as="p">{consignment.dischargeCountry}</Text>
                    </div>
                </>)
            }
        </div>
    </Card>
);


const DeclarationTab = ({docs}: { docs: CustomsStatusDocument[] }) => {
    const tableRef = useRef<TableRef>(null);
    return (
        <Card>
            <DeclarationStatusTable data={docs} tableRef={tableRef}/>
        </Card>
    );
}


const ExceptionsTab = ({
                           exceptions,
                           dispatch,
                           expandedStep,
                           onExpandStep,
                       }: {
    exceptions: Exception[];
    dispatch: React.Dispatch<ConsignmentPageAction>;
    expandedStep?: string | null;
    onExpandStep?: (stepTitle: string) => void;
}) => {
    const exceptionsByStep = exceptions.reduce((acc, ex, idx) => {
        const stepKey = encodeURIComponent(ex.stepTitle || "Unknown Step " + idx);
        if (!acc[stepKey]) {
            acc[stepKey] = [];
        }
        acc[stepKey].push(ex);
        return acc;
    }, {} as Record<string, Exception[]>);

    return (
        <Card className="eds-spacing--p-24">
            {exceptions.length === 0 && <Text font="title-4" as="h4" className="eds-spacing--mt-56 eds-spacing--mb-56" style={{textAlign: "center"}}>No exceptions found for this consignment.</Text>}
            {Object.keys(exceptionsByStep).map(stepKey => {
                const group = exceptionsByStep[stepKey];
                const stepTitle = group[0]?.stepTitle || decodeURIComponent(stepKey);
                const isOpen = encodeURIComponent(expandedStep || '') === stepKey || (!expandedStep && Object.keys(exceptionsByStep).length === 1);
                return (
                    <Collapsible
                        title={stepTitle}
                        key={stepKey}
                        isOpen={isOpen}

                    >
                        <ul>
                            {group.map((ex: Exception) => (
                                <li key={ex.id} className="eds-spacing--mb-8">
                                    <Link className="" font={'body-2'} target='_blank' href={`/exceptions/${encodeURIComponent(ex.id)}`}>
                                        {ex.alias}: {ex.description}
                                    </Link>
                                    <Tag
                                        className="eds-spacing--ml-32"
                                        color={mapStatusToColor(ex.status)}
                                        size="small"
                                        style={{whiteSpace: "nowrap", display: "inline-block", textAlign: "center", width: "auto"}}
                                    >{ex.status.replace(/_/g, " ")}</Tag>
                                </li>

                            ))}
                        </ul>
                    </Collapsible>
                );
            })}
        </Card>
    )
};

const AuditTrailTab = ({auditTrailItems}: { auditTrailItems: (ConsignmentAuditStepEntry | ConsignmentAuditExceptionEntry)[] }) => {
    if (!auditTrailItems || auditTrailItems.length === 0) {
        return (
            <Card className="eds-spacing--p-24">
                <Text font="title-4" as="h4" className="eds-spacing--mt-56 eds-spacing--mb-56" style={{textAlign: "center"}}>No audit trail entries found for this consignment.</Text>
            </Card>
        );
    }

    // Sort by timestamp descending (most recent first)
    const sortedItems = [...auditTrailItems].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    return (
        <Card className="eds-spacing--p-24">
            <ul>
                {sortedItems.map((item) => (
                    <li key={item.id} className="eds-spacing--mb-16">
                        <Text font="body-2" as="span">
                            <strong>{formatDateTime(item.timestamp)}</strong>
                            {" - "}
                            {"stepTitle" in item
                                ? <>Step: {item.stepTitle}</>
                                : <>Exception: {item.exceptionAlias} ({item.exceptionStatus})</>
                            }
                        </Text>
                    </li>
                ))}
            </ul>
        </Card>
    );
};
